import * as React from 'react'
import { handleImageUpload } from '@/tiptap/lib/tiptap-utils'
import { Editor } from '@tiptap/react'
import { Upload, Link, Image as ImageIcon, Sun, Moon } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface InlineImageDialogProps {
  editor: Editor
  isOpen: boolean
  onClose: () => void
  onUpload?: (file: File) => Promise<string>
  slashCommandRange?: { from: number; to: number } | null
  replaceMode?: boolean
  existingImage?: {
    src: string
    alt?: string
    title?: string
    darkSrc?: string
    display?: 'inline' | 'original' | 'block'
    nodePos?: number
  }
  isDarkModeReplace?: boolean
}

export function InlineImageDialog({
  editor,
  isOpen,
  onClose,
  onUpload,
  slashCommandRange,
  replaceMode = false,
  existingImage,
  isDarkModeReplace = false,
}: InlineImageDialogProps) {
  const [imageUrl, setImageUrl] = React.useState('')
  const [darkImageUrl, setDarkImageUrl] = React.useState('')
  const [altText, setAltText] = React.useState('')
  const [isUploading, setIsUploading] = React.useState(false)
  const [uploadedUrl, setUploadedUrl] = React.useState('')
  const [uploadedDarkUrl, setUploadedDarkUrl] = React.useState('')
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const darkFileInputRef = React.useRef<HTMLInputElement>(null)
  const [tab, setTab] = React.useState<'url' | 'upload'>('url')

  // Initialize with existing image data when dialog opens in replace mode
  React.useEffect(() => {
    if (isOpen && replaceMode && existingImage) {
      setImageUrl(isDarkModeReplace ? '' : existingImage.src || '')
      setDarkImageUrl(existingImage.darkSrc || '')
      setAltText(existingImage.alt || '')
    }
  }, [isOpen, replaceMode, existingImage, isDarkModeReplace])

  const handleFileUpload = async (file: File, isDark = false) => {
    // Use custom upload handler if provided, otherwise use default
    const uploadHandler = onUpload || handleImageUpload

    setIsUploading(true)
    try {
      const url = await uploadHandler(file)
      if (isDark) {
        setUploadedDarkUrl(url)
        setDarkImageUrl(url)
      } else {
        setUploadedUrl(url)
        setImageUrl(url)
      }
      toast.success(
        isDark ? 'Dark mode image uploaded' : 'Image uploaded successfully'
      )
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error('Failed to upload image')
    } finally {
      setIsUploading(false)
    }
  }

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isDark = false
  ) => {
    const file = e.target.files?.[0]
    if (file && /image/i.test(file.type)) {
      handleFileUpload(file, isDark)
    }
  }

  const handleInsert = () => {
    const src = imageUrl || uploadedUrl
    const darkSrc = darkImageUrl || uploadedDarkUrl

    // In replace mode, we need either a new src or a new darkSrc
    if (replaceMode) {
      if (isDarkModeReplace && !darkSrc) {
        toast.error('Please provide a dark mode image URL or upload an image')
        return
      } else if (!isDarkModeReplace && !src) {
        toast.error('Please provide an image URL or upload an image')
        return
      }
    } else if (!src) {
      toast.error('Please provide an image URL or upload an image')
      return
    }

    // Handle replace mode
    if (replaceMode && existingImage && existingImage.nodePos !== undefined) {
      const updateAttrs: any = {
        alt: altText || existingImage.alt || 'image',
      }

      if (isDarkModeReplace) {
        updateAttrs.darkSrc = darkSrc
      } else {
        updateAttrs.src = src
        if (darkSrc) updateAttrs.darkSrc = darkSrc
      }

      editor
        .chain()
        .focus()
        .setNodeSelection(existingImage.nodePos)
        .updateAttributes('inlineImage', updateAttrs)
        .run()
    } else if (slashCommandRange) {
      // If this is from a slash command, delete the slash command text first
      editor
        .chain()
        .focus()
        .deleteRange(slashCommandRange)
        .insertInlineImage({
          src,
          alt: altText || 'image',
          darkSrc: darkSrc || undefined,
          display: 'inline',
        })
        .run()
    } else {
      // Normal insertion without slash command
      editor
        .chain()
        .focus()
        .insertInlineImage({
          src,
          alt: altText || 'image',
          darkSrc: darkSrc || undefined,
          display: 'inline',
        })
        .run()
    }

    handleClose()
  }

  const handleClose = () => {
    setImageUrl('')
    setDarkImageUrl('')
    setAltText('')
    setUploadedUrl('')
    setUploadedDarkUrl('')
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className='gap-0 rounded-2xl border border-sidebar-border bg-white p-0 shadow-[0_8px_40px_0_rgba(0,0,0,0.08)] sm:max-w-[500px]'
        closeClassName='top-5 right-6 rounded-md p-1 text-[#60646D] opacity-100 hover:bg-muted hover:text-[#43464D]'
      >
        <DialogHeader className='gap-1 border-b border-sidebar-border px-6 py-5'>
          <DialogTitle className='font-[Fraunces] text-[20px] leading-[1.2] font-medium text-sidebar-foreground'>
            {replaceMode
              ? isDarkModeReplace
                ? 'Replace Dark Mode Image'
                : 'Replace Image'
              : 'Insert Inline Image'}
          </DialogTitle>
          <DialogDescription className='text-[13px] leading-[1.3] font-normal text-[#7E838C]'>
            'Paste an image URL or choose a file to upload'
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 px-6 pt-[14px] pb-0'>
          <div className='space-y-2'>
            <Label
              htmlFor='alt-text'
              className='text-[13px] leading-[1.2] font-medium text-sidebar-foreground'
            >
              Alt Text
            </Label>
            <Input
              id='alt-text'
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder='Describe the image...'
              className='mt-1 h-9 rounded-lg border-[#D8DCE4] bg-white px-3 text-[14px] font-normal text-[#3F444D] placeholder:text-[#A0A5AF]'
            />
          </div>

          <Tabs
            value={tab}
            onValueChange={(value) => setTab(value as 'url' | 'upload')}
            className='w-full gap-[14px]'
          >
            <TabsList className='grid h-9 w-full grid-cols-2 rounded-lg bg-[#EFEFED] p-[3px]'>
              <TabsTrigger
                value='url'
                className='rounded-md h-full text-[13px] leading-[1.1] font-normal data-[state=active]:bg-white data-[state=active]:text-[#383C44] data-[state=inactive]:bg-transparent data-[state=inactive]:text-[#8B8F98]'
              >
                <Link className='mr-0.5 h-4 w-4' />
                URL
              </TabsTrigger>
              <TabsTrigger
                value='upload'
                className='rounded-md h-full text-[13px] leading-[1.1] font-normal data-[state=active]:bg-white data-[state=active]:text-[#383C44] data-[state=inactive]:bg-transparent data-[state=inactive]:text-[#8B8F98]'
              >
                <Upload className='mr-0.5 h-4 w-4' />
                Upload
              </TabsTrigger>
            </TabsList>

            <TabsContent value='url' className='space-y-4'>
              <div className='space-y-2'>
                <Label
                  htmlFor='image-url'
                  className='text-[13px] leading-[1.2] font-medium text-sidebar-foreground'
                >
                  Image URL
                </Label>
                <Input
                  id='image-url'
                  type='url'
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder='https://example.com/image.png'
                  className='mt-1 h-9 rounded-lg border-[#D8DCE4] bg-white px-3 text-[14px] font-normal text-[#3F444D] placeholder:text-[#A0A5AF]'
                />
              </div>

              {/* <div className='space-y-2'>
                <Label
                  htmlFor='dark-image-url'
                  className='text-[14px] leading-[1.2] font-normal text-[#666C75]'
                >
                  Dark Mode Image URL (Optional)
                </Label>
                <Input
                  id='dark-image-url'
                  type='url'
                  value={darkImageUrl}
                  onChange={(e) => setDarkImageUrl(e.target.value)}
                  placeholder='https://example.com/image-dark.png'
                  className='mt-1 h-9 rounded-lg border-[#D8DCE4] bg-white px-3 text-[14px] font-normal text-[#3F444D] placeholder:text-[#A0A5AF]'
                />
              </div> */}
            </TabsContent>

            <TabsContent value='upload'>
              <div className='space-y-2'>
                  <Label className='text-[13px] leading-[1.2] font-medium text-sidebar-foreground'>
                  Upload image
                  </Label>

                <div className='flex w-full items-center gap-2'>
                  <div>
                    <input
                      ref={fileInputRef}
                      type='file'
                      accept='image/*'
                      onChange={(e) => handleFileChange(e, false)}
                      className='hidden'
                    />
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      size='md'
                      className='text-sm leading-5 font-medium'
                    >
                      <Upload className='h-4 w-4' />
                      Choose Image
                    </Button>
                  </div>
                  {/* 
                  {uploadedUrl && (
                    <span className='text-muted-foreground text-sm'>
                      Image uploaded
                    </span>
                  )} */}
                  <div>
                    <input
                      ref={darkFileInputRef}
                      type='file'
                      accept='image/*'
                      onChange={(e) => handleFileChange(e, true)}
                      className='hidden'
                    />
                    {/* <Button
                      type='button'
                      variant='outline'
                      size='md'
                      onClick={() => darkFileInputRef.current?.click()}
                      disabled={isUploading}
                      className='text-sm leading-5 font-medium'
                    >
                      <Upload className='h-4 w-4' />
                      Choose Dark Image
                    </Button> */}
                    {/* {uploadedDarkUrl && (
                        <span className='text-muted-foreground text-sm'>
                          Dark image uploaded
                        </span>
                      )} */}
                  </div>
                </div>
                    {/* <span className='text-[13px] leading-5 text-[#7E838C]'>
                  A default image will be used in dark mode if a dark version is
                  not provided.
                </span> */}
              </div>
            </TabsContent>
          </Tabs>

          {/* {(imageUrl || uploadedUrl) && (
            <div className='bg-muted/50 rounded-lg border p-4'>
              <Label className='text-sm'>Preview</Label>
              <div className='mt-2 flex items-center gap-4'>
                <img
                  src={imageUrl || uploadedUrl}
                  alt={altText || 'Preview'}
                  className='inline max-h-8 w-auto rounded'
                />
                {(darkImageUrl || uploadedDarkUrl) && (
                  <>
                    <span className='text-muted-foreground text-sm'>→</span>
                    <div className='dark'>
                      <img
                        src={darkImageUrl || uploadedDarkUrl}
                        alt={altText || 'Dark mode preview'}
                        className='inline max-h-8 w-auto rounded'
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )} */}

          {/* Preview */}
          {(imageUrl || uploadedUrl) && (
            <div className='space-y-2'>
              <Label className='text-[13px] leading-[1.2] font-medium text-sidebar-foreground'>
                Preview
              </Label>
              <div className='bg-background flex items-center justify-between gap-6 rounded-lg border p-4'>
                <div className='flex-1 space-y-2'>
                  {/* <div className='flex w-full items-center gap-2'>
                    <Sun className='text-muted-foreground h-4 w-4' />
                    <span className='text-muted-foreground text-xs leading-4'>
                      Light Mode
                    </span>
                  </div> */}
                  <img
                    src={imageUrl || uploadedUrl}
                    alt={altText || 'Preview'}
                    className='inline max-h-20 w-auto rounded-md'
                  />
                </div>
                {(darkImageUrl || uploadedDarkUrl) && (
                  <div className='flex-1 space-y-2'>
                    <div className='flex w-full items-center gap-2'>
                      <Moon className='text-muted-foreground h-4 w-4' />
                      <span className='text-muted-foreground text-xs leading-4'>
                        Dark Mode
                      </span>
                    </div>
                    <img
                      src={darkImageUrl || uploadedDarkUrl}
                      alt={altText || 'Dark mode preview'}
                      className='inline max-h-20 w-auto rounded-md'
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className='justify-end gap-[10px] px-6 pt-[14px] pb-4'>
          <Button
            variant='outline'
            onClick={handleClose}
            className='h-9 w-[74px] rounded-lg border-[#E3E1DD] px-0 text-[13px] font-normal text-[#43464D] hover:bg-muted'
          >
            Cancel
          </Button>
          <Button
            onClick={handleInsert}
            disabled={replaceMode ? false : !imageUrl && !uploadedUrl}
            className='h-9 w-[96px] rounded-lg bg-sidebar-primary px-0 text-[13px] font-normal text-white hover:bg-[#6f8264]'
          >
            {replaceMode ? 'Replace Image' : 'Insert Image'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
